import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { MdClose } from "react-icons/md";
import { metaMask } from "wagmi/connectors";
import { ImageLoader } from "components/ImageLoader";
import { Box } from "@mui/material";
import { Wallets, walletsMap } from "constants/wallets";
import { WalletButton } from "@rainbow-me/rainbowkit";
import { WalletActionButton } from "./components/WalletActionButton";
import WalletConnectIcon from "components/SvgComponents/ConnectWalletIcon";
import TrustWalletIcon from "components/SvgComponents/TrustWalletIcon";
import MetamaskWallet from "components/SvgComponents/MetamaskIcon";
import { useConnect } from "wagmi";
import { useNetworksStore } from "store/networksStore";
import { isSolanaNetwork } from "types/networks";
import { useSolanaWallet } from "hooks/useSolanaWallet";
import type { SolanaWalletId } from "types/solanaWallet";
import SolanaPhantomIcon from "components/SvgComponents/SolanaPhantomIcon";
import SolanaSolflareIcon from "components/SvgComponents/SolanaSolflareIcon";
import SolanaBackpackIcon from "components/SvgComponents/SolanaBackpackIcon";

interface AdminFormProps {
  children?: React.ReactNode;
}

const getSolanaWalletIcon = (walletId: SolanaWalletId) => {
  switch (walletId) {
    case "phantom":
      return <SolanaPhantomIcon props={{}} />;
    case "solflare":
      return <SolanaSolflareIcon props={{}} />;
    case "backpack":
      return <SolanaBackpackIcon props={{}} />;
    case "trustWallet":
      return <TrustWalletIcon props={{}} />;
    default:
      return null;
  }
};

export function ConnectWalletDialog({ children }: AdminFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [solanaWalletError, setSolanaWalletError] = useState("");
  const { currentNetwork } = useNetworksStore();
  const isCurrentSolanaNetwork = isSolanaNetwork(currentNetwork);
  const { connectWallet, availableWallets } = useSolanaWallet();
  const { connectors, connectAsync, connect } = useConnect();

  const handleConnect = async (fn: () => Promise<unknown> | void) => {
    try {
      await Promise.resolve(fn());
      setIsOpen(false);
      setSolanaWalletError("");
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        setSolanaWalletError(error.message);
      }
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setIsOpen(isOpen);
    setSolanaWalletError("");

    if (isOpen) return;
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} modal>
        <Dialog.Trigger asChild>{children}</Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-50 bg-gray-800 bg-opacity-50" />
          <Dialog.Content className="data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] z-[60] max-h-[85vh] w-[90vw] max-w-[450px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none dark:bg-gray-800 dark:text-white">
            <Dialog.Title className="text-mauve12 mb-10 text-[17px] font-medium">
              Choose your wallet
            </Dialog.Title>
            <ul className="flex flex-col gap-[16px]">
              {isCurrentSolanaNetwork ? (
                <>
                  {availableWallets.map((wallet) => (
                    <WalletActionButton
                      key={wallet.id}
                      onClick={() => {
                        if (!wallet.isInstalled) {
                          window.open(wallet.installUrl, "_blank", "noopener,noreferrer");
                          return;
                        }

                        handleConnect(() => connectWallet(wallet.id));
                      }}
                      walletName={
                        wallet.isInstalled
                          ? wallet.name
                          : `${wallet.name} (Install)`
                      }
                      icon={getSolanaWalletIcon(wallet.id)}
                    />
                  ))}

                  {solanaWalletError && (
                    <span className="text-sm text-red-500 dark:text-red-400">
                      {solanaWalletError}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <WalletActionButton
                    onClick={() =>
                      handleConnect(() => connect({ connector: metaMask() }))
                    }
                    walletName={walletsMap[Wallets.METAMASK].name}
                    icon={<MetamaskWallet props={{}} />}
                  />

                  <WalletButton.Custom wallet="trust">
                    {({ ready, connect }) => {
                      const cconnectorInjected = connectors.find(
                        (connector) => connector.id === Wallets.TrustWallet
                      );

                      return (
                        <WalletActionButton
                          disabled={!ready}
                          onClick={() =>
                            handleConnect(() =>
                              cconnectorInjected
                                ? connectAsync({ connector: cconnectorInjected })
                                : connect()
                            )
                          }
                          walletName={walletsMap[Wallets.TrustWallet].name}
                          icon={<TrustWalletIcon props={{}} />}
                        />
                      );
                    }}
                  </WalletButton.Custom>

                  <WalletButton.Custom wallet="walletConnect">
                    {({ ready, connect }) => {
                      return (
                        <WalletActionButton
                          disabled={!ready}
                          onClick={() => handleConnect(() => connect())}
                          walletName={walletsMap[Wallets.WalletConnect].name}
                          icon={<WalletConnectIcon props={{}} />}
                        />
                      );
                    }}
                  </WalletButton.Custom>

                  <WalletButton.Custom wallet="tokenPocket">
                    {({ ready, connect }) => {
                      const cconnectorInjected = connectors.find(
                        (connector) => connector.id === Wallets.TokenPocket
                      );
                      return (
                        <WalletActionButton
                          disabled={!ready}
                          onClick={() =>
                            handleConnect(() =>
                              cconnectorInjected
                                ? connectAsync({ connector: cconnectorInjected })
                                : connect()
                            )
                          }
                          walletName={walletsMap[Wallets.TokenPocket].name}
                          icon={
                            <ImageLoader src={"/images/tokenPocket.webp"}>
                              <Box
                                component={"img"}
                                sx={{
                                  height: "auto",
                                  width: "100%",
                                  maxWidth: { xs: "32px", sm: "48px" },
                                }}
                                src={"/images/tokenPocket.webp"}
                                alt={`${walletsMap[Wallets.TokenPocket].name} Icon`}
                              />
                            </ImageLoader>
                          }
                        />
                      );
                    }}
                  </WalletButton.Custom>
                </>
              )}
            </ul>

            <Dialog.Close asChild>
              <button
                type="button"
                className="text-violet11 hover:bg-violet4 focus:shadow-violet7 absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
                aria-label="Close"
              >
                <MdClose />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
